import type { KeyboardEvent, MouseEvent } from 'react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import type { StorefrontNavItem } from '@/utils/shopStorefrontNav'

function pathnameOnly(path: string): string {
  const i = path.indexOf('#')
  return i === -1 ? path : path.slice(0, i)
}

/** Same route without hash: scroll to top when RR would otherwise no-op. */
function scrollTopIfSamePage(
  e: MouseEvent<HTMLAnchorElement>,
  location: { pathname: string; hash: string },
  targetPath: string
) {
  const path = pathnameOnly(targetPath)
  if (location.pathname !== path) return
  if (location.hash) return
  e.preventDefault()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const HOVER_CLOSE_MS = 220

type NavItemWithDropdownProps = {
  item: StorefrontNavItem & { children: NonNullable<StorefrontNavItem['children']> }
  location: ReturnType<typeof useLocation>
  menuKey: string
  isOpen: boolean
  onOpenMenu: (key: string) => void
  onScheduleClose: () => void
  onToggleChevron: (key: string) => void
  primaryColor: string
}

function NavItemWithDropdown({
  item,
  location,
  menuKey,
  isOpen,
  onOpenMenu,
  onScheduleClose,
  onToggleChevron,
  primaryColor,
}: NavItemWithDropdownProps) {
  const itemPath = pathnameOnly(item.to)
  const onPage = location.pathname === itemPath
  const panelId = useId()
  const buttonId = useId()

  return (
    <div
      className="relative inline-flex items-stretch rounded-md transition-colors hover:bg-black/[0.04] focus-within:bg-black/[0.04]"
      onMouseEnter={() => onOpenMenu(menuKey)}
      onMouseLeave={onScheduleClose}
    >
      <Link
        to={item.to}
        onClick={(e) => scrollTopIfSamePage(e, location, item.to)}
        className={`shop-nav-link rounded-l-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--shop-primary)] focus-visible:ring-offset-2 ${
          onPage ? '' : 'text-slate-600 hover:text-[color:var(--shop-primary)]'
        }`}
        style={onPage ? { color: primaryColor } : undefined}
      >
        {item.label}
      </Link>
      <button
        type="button"
        id={buttonId}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={panelId}
        className={`shop-nav-link rounded-r-md pl-0.5 pr-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--shop-primary)] ${
          onPage ? '' : 'text-slate-600 hover:text-[color:var(--shop-primary)]'
        }`}
        style={onPage ? { color: primaryColor } : undefined}
        aria-label={`${item.label}: section links`}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onToggleChevron(menuKey)
        }}
        onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggleChevron(menuKey)
          }
        }}
      >
        <ChevronDownIcon
          className={`shop-nav-chevron shrink-0 opacity-80 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {/* pt-1 bridges a small gap so moving pointer into the panel does not fire mouseleave on the row */}
      <div
        id={panelId}
        role="menu"
        aria-labelledby={buttonId}
        className={`absolute right-0 top-full z-30 pt-1 ${isOpen ? 'block' : 'hidden'}`}
      >
        <div className="min-w-[14rem] rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5">
          {item.children.map((c) => (
            <Link
              key={`${c.to}::${c.label}`}
              role="menuitem"
              to={c.to}
              className="shop-nav-submenu-link"
            >
              {c.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

export function ShopStorefrontNav({
  items,
  primaryColor = '#4f46e5',
}: {
  items: StorefrontNavItem[]
  primaryColor?: string
}) {
  const location = useLocation()
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null)
  const closeTimerRef = useRef<number | null>(null)
  const navClusterRef = useRef<HTMLDivElement>(null)

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const openMenu = useCallback(
    (key: string) => {
      clearCloseTimer()
      setOpenMenuKey(key)
    },
    [clearCloseTimer]
  )

  const scheduleCloseMenu = useCallback(() => {
    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(() => setOpenMenuKey(null), HOVER_CLOSE_MS)
  }, [clearCloseTimer])

  const toggleChevron = useCallback(
    (key: string) => {
      clearCloseTimer()
      setOpenMenuKey((k) => (k === key ? null : key))
    },
    [clearCloseTimer]
  )

  useEffect(() => {
    if (!openMenuKey) return
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearCloseTimer()
        setOpenMenuKey(null)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [openMenuKey, clearCloseTimer])

  useEffect(() => {
    if (!openMenuKey) return
    const onPointerDown = (e: PointerEvent) => {
      const node = e.target as Node
      if (navClusterRef.current?.contains(node)) return
      clearCloseTimer()
      setOpenMenuKey(null)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [openMenuKey, clearCloseTimer])

  useEffect(() => {
    return () => clearCloseTimer()
  }, [clearCloseTimer])

  useEffect(() => {
    setOpenMenuKey(null)
  }, [location.pathname, location.hash])

  return (
    <div ref={navClusterRef} className="contents">
      {items.map((item) => {
        const itemPath = pathnameOnly(item.to)
        const menuKey = `${item.to}::${item.label}`

        return item.children && item.children.length > 0 ? (
          <NavItemWithDropdown
            key={`${item.to}-${item.label}`}
            item={item as NavItemWithDropdownProps['item']}
            location={location}
            menuKey={menuKey}
            isOpen={openMenuKey === menuKey}
            onOpenMenu={openMenu}
            onScheduleClose={scheduleCloseMenu}
            onToggleChevron={toggleChevron}
            primaryColor={primaryColor}
          />
        ) : (
          <Link
            key={`${item.to}-${item.label}`}
            to={item.to}
            onClick={(e) => scrollTopIfSamePage(e, location, item.to)}
            className={`shop-nav-link transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--shop-primary)] focus-visible:ring-offset-2 ${
              location.pathname === itemPath
                ? ''
                : 'text-slate-600 hover:text-[color:var(--shop-primary)]'
            }`}
            style={location.pathname === itemPath ? { color: primaryColor } : undefined}
          >
            {item.label}
          </Link>
        )
      })}
    </div>
  )
}
